import { Button, Box, Typography, Switch, IconButton, useMediaQuery, useTheme, CircularProgress, Drawer } from '@mui/material'
import { useContext, useState, useMemo, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Print from '@mui/icons-material/Print'
import PrintOutlined from '@mui/icons-material/PrintOutlined'
import CloseIcon from '@mui/icons-material/Close'
import {
  selectEmrSectionsData,
  selectPrescriptionViewSettingsData,
} from 'src/clinic/reducers/prescription/prescriptionReducer'
import { PrescriptionDataContext } from 'src/mentor/components/PrescriptionMakingPage/context/PrescriptionDataProvider'
import PrescriptionViewComponent from 'src/mentor/components/PrescriptionMakingPage/printComponents/PrescriptionViewComponent'
import usePrescriptionViewSettingsForRx from 'src/mentor/components/PrescriptionMakingPage/viewOnlyComponents/hooks/usePrescriptionViewSettingsForRx'
import PageSelectionMenu from 'src/mentor/components/PrescriptionMakingPage/printComponents/components/PageSelectionMenu'
import A4PrintSettings from 'src/mentor/components/PrescriptionMakingPage/printComponents/printSettings/A4PrintSettings'
import { PMPPrintWrapper, PMPSectionHeading } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { A4DpiToDetailsMap } from 'src/consumer/constants'
import { isPWA } from 'src/services/mediaPermissions/permissionConstants'
import {
  getInitialDataForSettingDaysOfNextVisit,
  getInitialDataForSettingExistingDrugAdvice,
  getInitialDataForSettingExistingSelectedAdvice,
  getInitialDataForSettingExistingSelectedComplaints,
  getInitialDataForSettingExistingSelectedDiagnosis,
  getInitialDataForSettingExistingSelectedInstructions,
  getInitialDataForSettingExistingSelectedInvestigations,
  getInitialDataForSettingExistingSelectedObservations,
  getInitialDataForSettingExistingSelectedTherapies,
  getInitialDataForSettingExistingHealthEntity,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'

export default function RXDrawerComponent({ 
  mentorProfileData, 
  auxiliaryData, 
  bookedEventData,
  prescriptionRef, // Optional ref to prescription component for print functions
  onClose, // Optional callback when drawer should close
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const prescriptionViewSettings = useSelector(selectPrescriptionViewSettingsData)
  const emrSections = useSelector(selectEmrSectionsData)

  const prescriptionViewSettingsData = prescriptionViewSettings?.data
  const emrSectionsData = emrSections?.data?.prescription?.emrSections

  // Use auxiliaryData?.metaPrescription as primary source, fallback to context
  const metaPrescriptionData = auxiliaryData?.metaPrescription

  const { printSettings, setPrintSettings } = usePrescriptionViewSettingsForRx({
    metaPrescription: metaPrescriptionData,
    prescriptionViewSettings: prescriptionViewSettingsData,
  })

  // State for color mode and total pages
  const [colorMode, setColorMode] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const savedColorMode = window.localStorage.getItem('prescriptionColorMode')
      return savedColorMode === 'bw' ? 'bw' : 'colored'
    }
    return 'colored'
  })
  const [totalPages, setTotalPages] = useState(0)
  const [isPrinting, setIsPrinting] = useState(false)
  const [dimensionRefreshTrigger, setDimensionRefreshTrigger] = useState(0) // Trigger to force dimension refresh
  const printTimeoutRef = useRef(null)
  const scrollableContentRef = useRef(null)
  const totalPagesRef = useRef(0) // Ref to track current totalPages for closure access

  // Save colorMode to localStorage when it changes
  const handleColorModeChange = (newMode) => {
    setColorMode(newMode)
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('prescriptionColorMode', newMode)
    }
  }
  
  // Listen for print dialog events to reset loading state
  useEffect(() => {
    const handleBeforePrint = () => {
      console.log('[Rx Drawer] Print dialog opened, resetting loading state')
      setIsPrinting(false)
      if (printTimeoutRef.current) {
        window.clearTimeout(printTimeoutRef.current)
        printTimeoutRef.current = null
      }
    }
    
    const handleAfterPrint = () => {
      console.log('[Rx Drawer] Print dialog closed')
      setIsPrinting(false)
      if (printTimeoutRef.current) {
        window.clearTimeout(printTimeoutRef.current)
        printTimeoutRef.current = null
      }
    }
    
    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)
    
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
      if (printTimeoutRef.current) {
        window.clearTimeout(printTimeoutRef.current)
      }
    }
  }, [])

  // PDF generation function for Rx drawer (when ref is not available)
  const generatePDFFromDrawer = async (selectedPages = null, autoPrint = false) => {
    console.log('[Rx Drawer] generatePDFFromDrawer called', { selectedPages, autoPrint })
    
    try {
      // Get A4 dimensions from printSettings
      const currentDpiData = A4DpiToDetailsMap?.[printSettings?.dpi] || A4DpiToDetailsMap[96]
      const widthOfA4Page = currentDpiData?.width || 794
      
      // Get all prescription pages from the drawer
      let allPages = Array.from(document.querySelectorAll('.prescription-page'))
      
      // Filter pages if specific pages are selected
      if (selectedPages && selectedPages.length > 0) {
        allPages = allPages.filter((page, idx) => {
          const pageNum = parseInt(page.getAttribute('data-page-number')) || (idx + 1)
          return selectedPages.includes(pageNum)
        })
      }
      
      if (!allPages || allPages.length === 0) {
        toast.error('No prescription pages found to print', { autoClose: false })
        return
      }
      
      console.log(`[Rx Drawer] Found ${allPages.length} page(s) to process`)
      
      // Sort pages by data-page-number
      allPages.sort((a, b) => {
        const pageNumA = parseInt(a.getAttribute('data-page-number')) || 0
        const pageNumB = parseInt(b.getAttribute('data-page-number')) || 0
        return pageNumA - pageNumB
      })
      
      toast.info('Generating PDF...', { autoClose: 2000 })
      
      // Import jsPDF and html2canvas
      const { jsPDF } = await import('jspdf')
      const html2canvas = (await import('html2canvas')).default
      
      const pdf = new jsPDF({
        unit: 'px',
        format: 'a4',
        orientation: 'portrait',
        compress: true,
      })
      
      const pdfPageWidth = pdf.internal.pageSize.getWidth()
      const pdfPageHeight = pdf.internal.pageSize.getHeight()
      // Reduced default scale from 1.5 to 1.0 to reduce file size while maintaining good print quality
      const canvasScale = printSettings?.canvasScale || 1.0
      
      let pdfPageCount = 0
      
      for (let idx = 0; idx < allPages.length; idx++) {
        const pageElement = allPages[idx]
        if (!pageElement) continue
        
        if (pdfPageCount > 0) {
          pdf.addPage()
        }
        pdfPageCount++
        
        // Batch layout reads
        const pageRect = pageElement.getBoundingClientRect()
        const scrollWidth = pageElement.scrollWidth || pageRect.width
        const scrollHeight = pageElement.scrollHeight || pageRect.height
        
        const allChildren = Array.from(pageElement.querySelectorAll('*'))
        const childMeasurements = allChildren.map(child => {
          const childRect = child.getBoundingClientRect()
          return {
            scrollWidth: child.scrollWidth || childRect.width,
            width: childRect.width
          }
        })
        
        let maxChildWidth = scrollWidth
        childMeasurements.forEach(measurement => {
          if (measurement.scrollWidth > maxChildWidth) {
            maxChildWidth = measurement.scrollWidth
          }
        })
        
        const captureWidth = Math.max(maxChildWidth, scrollWidth, widthOfA4Page)
        const captureHeight = Math.min(scrollHeight, pdfPageHeight)
        
        const originalWidth = pageElement.style.width
        const originalMaxWidth = pageElement.style.maxWidth
        pageElement.style.width = `${captureWidth}px`
        pageElement.style.maxWidth = 'none'
        
        try {
          const canvas = await html2canvas(pageElement, {
            scale: canvasScale,
            useCORS: false,
            logging: false,
            letterRendering: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            width: captureWidth,
            height: captureHeight,
            windowWidth: captureWidth,
            windowHeight: captureHeight,
            x: 0,
            y: 0,
            scrollX: 0,
            scrollY: 0,
            removeContainer: false,
          })
          
          const canvasActualWidth = canvas.width / canvasScale
          const canvasActualHeight = canvas.height / canvasScale
          const scaleX = pdfPageWidth / canvasActualWidth
          const scaleY = pdfPageHeight / canvasActualHeight
          const scale = Math.min(scaleX, scaleY)
          const finalWidth = canvasActualWidth * scale
          const finalHeight = canvasActualHeight * scale
          
          // Optimized quality (0.85) for good balance between file size and print quality
          const imgData = canvas.toDataURL('image/jpeg', 0.85)
          pdf.addImage(imgData, 'JPEG', 0, 0, finalWidth, finalHeight, undefined, 'FAST')
          
          // Clean up canvas
          const ctx = canvas.getContext('2d')
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
          }
          canvas.width = 0
          canvas.height = 0
          
          // Restore original styles
          pageElement.style.width = originalWidth
          pageElement.style.maxWidth = originalMaxWidth
        } catch (error) {
          console.error(`[Rx Drawer] Error processing page ${idx + 1}:`, error)
          pageElement.style.width = originalWidth
          pageElement.style.maxWidth = originalMaxWidth
          continue
        }
      }
      
      // Generate PDF blob and open
      const pdfBlob = pdf.output('blob')
      const pdfUrl = window.URL.createObjectURL(pdfBlob)
      
      console.log('[Rx Drawer] PDF generated successfully', { pdfUrl, autoPrint })
      
      if (autoPrint) {
        // Open in new window and print
        const printWindow = window.open(pdfUrl, '_blank')
        if (printWindow) {
          printWindow.onload = () => {
            window.setTimeout(() => {
              printWindow.print()
            }, 500)
          }
        } else {
          toast.error('Popup blocked. Please allow popups and try again.', { autoClose: 5000 })
        }
      } else {
        // Open in new tab for preview
        window.open(pdfUrl, '_blank')
      }
      
      toast.success('PDF generated successfully!', { autoClose: 2000 })
    } catch (error) {
      console.error('[Rx Drawer] Error generating PDF:', error)
      toast.error(`Failed to generate PDF: ${error.message || 'Unknown error'}`, { autoClose: 5000 })
    }
  }

  // Quick print function - clones pages and opens browser print preview directly
  const handleQuickPrint = async () => {
    console.log('[Rx Drawer] Quick print called - cloning pages and opening print preview')
    
    try {
      // Get A4 dimensions from printSettings (same as PDF generation)
      const currentDpiData = A4DpiToDetailsMap?.[printSettings?.dpi] || A4DpiToDetailsMap[96]
      const widthOfA4Page = currentDpiData?.width || 794
      const heightOfA4Page = currentDpiData?.height || 1123 // A4 height for footer positioning
      
      // Get all prescription pages - use same logic as PDF generation
      let allPages = Array.from(document.querySelectorAll('.prescription-page'))
      
      // Filter out pages from hidden containers (same logic as PDF generation)
      allPages = allPages.filter(page => {
        // Check if page itself is visible
        const pageStyle = window.getComputedStyle(page)
        if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
          return false
        }
        
        // Check if page is in a visible container (not hidden drawer/modal)
        let parent = page.parentElement
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent)
          // Skip if parent is hidden
          if (style.display === 'none' || style.visibility === 'hidden') {
            return false
          }
          // Skip if parent is a closed drawer (MUI Drawer with display: none)
          if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
            return false
          }
          parent = parent.parentElement
        }
        return true
      })
      
      // Deduplicate by page number - same logic as PDF generation
      const pagesByNumber = new Map()
      allPages.forEach(page => {
        const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page')
        if (pageNum) {
          // If we already have a page with this number, prefer the one not in PMPPrintWrapper
          // (main page takes priority over Rx drawer)
          if (!pagesByNumber.has(pageNum)) {
            pagesByNumber.set(pageNum, page)
          } else {
            const existingPage = pagesByNumber.get(pageNum)
            const isExistingInDrawer = existingPage.closest('.PMPPrintWrapper') !== null
            const isCurrentInDrawer = page.closest('.PMPPrintWrapper') !== null
            
            // Prefer main page over drawer page
            if (isExistingInDrawer && !isCurrentInDrawer) {
              pagesByNumber.set(pageNum, page)
            }
          }
        } else {
          // If no page number, add it (shouldn't happen, but handle it)
          pagesByNumber.set(`no-number-${pagesByNumber.size}`, page)
        }
      })
      
      allPages = Array.from(pagesByNumber.values())
      
      // Sort pages by data-page-number to ensure correct order (same as PDF generation)
      allPages.sort((a, b) => {
        const pageNumA = parseInt(a.getAttribute('data-page-number') || a.getAttribute('data-pdf-page')) || 0
        const pageNumB = parseInt(b.getAttribute('data-page-number') || b.getAttribute('data-pdf-page')) || 0
        return pageNumA - pageNumB
      })
      
      console.log(`[Rx Drawer Quick Print] Found ${allPages.length} unique page(s) to process`)
      
      if (!allPages || allPages.length === 0) {
        toast.error('No prescription pages found to print', { autoClose: 3000 })
        return
      }
      
      // Helper function to copy computed styles
      const copyComputedStyles = (original, cloned) => {
        const computed = window.getComputedStyle(original)
        const styleProps = [
          'display', 'visibility', 'opacity', 'position',
          'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
          'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
          'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
          'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
          'textAlign', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'color',
          'backgroundColor', 'background', 'backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat',
          'boxSizing', 'flexDirection', 'flexWrap', 'justifyContent', 'alignItems', 'alignContent', 'flex', 'flexGrow', 'flexShrink', 'flexBasis',
          'textDecoration', 'textTransform', 'letterSpacing', 'wordSpacing',
          'verticalAlign', 'whiteSpace', 'overflow', 'overflowX', 'overflowY',
          'zIndex', 'transform', 'transformOrigin',
          'borderCollapse', 'borderSpacing', 'tableLayout', 'captionSide',
          'boxShadow', 'textShadow', 'outline', 'outlineWidth', 'outlineStyle', 'outlineColor',
        ]
        
        styleProps.forEach(prop => {
          const value = computed.getPropertyValue(prop)
          if (value && value !== 'none' && value !== 'normal' && value !== 'auto' && value !== '0px') {
            cloned.style.setProperty(prop, value)
          }
        })
        
        // Preserve borders if they exist
        const borderWidth = computed.borderWidth
        const borderStyle = computed.borderStyle
        const borderColor = computed.borderColor
        if (borderWidth && borderWidth !== '0px' && borderStyle && borderStyle !== 'none') {
          cloned.style.border = `${borderWidth} ${borderStyle} ${borderColor}`
        }
      }
      
      // Helper function to convert image to base64
      const imageToBase64 = async (url, originalImgElement = null) => {
        if (originalImgElement && originalImgElement.complete && originalImgElement.naturalHeight !== 0) {
          try {
            const imgUrl = new URL(originalImgElement.src, window.location.href)
            const isSameOrigin = imgUrl.origin === window.location.origin
            
            if (isSameOrigin || originalImgElement.crossOrigin !== null) {
              const canvas = document.createElement('canvas')
              canvas.width = originalImgElement.naturalWidth || originalImgElement.width
              canvas.height = originalImgElement.naturalHeight || originalImgElement.height
              const ctx = canvas.getContext('2d')
              ctx.drawImage(originalImgElement, 0, 0)
              return canvas.toDataURL('image/png')
            }
          } catch (e) {
            console.warn('Could not get image from DOM element:', url)
          }
        }
        
        try {
          const response = await fetch(url, { 
            mode: 'cors', 
            credentials: 'omit',
            headers: { 'Accept': 'image/*' }
          })
          if (response.ok) {
            const blob = await response.blob()
            return new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onloadend = () => resolve(reader.result)
              reader.onerror = () => reject(new Error('FileReader failed'))
              reader.readAsDataURL(blob)
            })
          }
        } catch (fetchError) {
          console.warn('Fetch failed for image:', url)
        }
        
        return null
      }
      
      // Clone pages with proper styling and image conversion
      const clonedPages = []
      for (let idx = 0; idx < allPages.length; idx++) {
        const pageElement = allPages[idx]
        if (!pageElement) continue
        
        const cloned = pageElement.cloneNode(true)
        
        // Remove hide-on-print elements
        const hideElements = cloned.querySelectorAll('.hide-on-print-only, .hide-on-print')
        hideElements.forEach(el => el.remove())
        
        // Convert images to base64
        const images = cloned.querySelectorAll('img')
        for (const clonedImg of images) {
          const src = clonedImg.getAttribute('src') || clonedImg.src
          if (src && !src.startsWith('data:')) {
            try {
              // Try to find original image element
              let originalImg = null
              try {
                const originalImages = pageElement.querySelectorAll('img')
                originalImg = Array.from(originalImages).find(img => {
                  try {
                    return img.src === src || 
                           img.getAttribute('src') === src ||
                           new URL(img.src, window.location.href).href === new URL(src, window.location.href).href
                  } catch (e) {
                    return img.src === src || img.getAttribute('src') === src
                  }
                })
              } catch (e) {
                console.warn('Error finding original image element:', e)
              }
              
              const base64 = await imageToBase64(src, originalImg)
              if (base64 && base64.startsWith('data:')) {
                clonedImg.src = base64
              }
            } catch (error) {
              console.error('Failed to convert image to base64:', src, error)
            }
          }
        }
        
        // Copy computed styles recursively
        const copyStylesRecursive = (orig, clone) => {
          copyComputedStyles(orig, clone)
          const origChildren = orig.children
          const cloneChildren = clone.children
          for (let i = 0; i < origChildren.length && i < cloneChildren.length; i++) {
            copyStylesRecursive(origChildren[i], cloneChildren[i])
          }
        }
        copyStylesRecursive(pageElement, cloned)
        
        // Remove padding/margins from A4 containers
        const a4Containers = cloned.querySelectorAll('.A4PageContainer, .A4PageMeasureComponentWrapperContainer, [id^="a4-"]')
        a4Containers.forEach(container => {
          container.style.padding = '0'
          container.style.margin = '0'
          container.style.width = '100%'
          container.style.maxWidth = '100%'
          container.style.minWidth = '100%'
          container.style.boxSizing = 'border-box'
          container.style.height = 'auto'
          container.style.maxHeight = 'none'
          container.style.minHeight = 'auto'
        })
        
        // Style the cloned page for print - use flex layout for footer positioning
        const originalComputed = window.getComputedStyle(pageElement)
        cloned.style.width = `${widthOfA4Page}px`
        cloned.style.maxWidth = `${widthOfA4Page}px`
        cloned.style.minWidth = `${widthOfA4Page}px`
        cloned.style.height = `${heightOfA4Page}px`
        cloned.style.minHeight = `${heightOfA4Page}px`
        cloned.style.maxHeight = `${heightOfA4Page}px`
        cloned.style.margin = '0 auto'
        cloned.style.padding = '0'
        cloned.style.display = 'flex'
        cloned.style.flexDirection = 'column'
        cloned.style.position = 'relative'
        cloned.style.boxSizing = 'border-box'
        cloned.style.overflow = 'hidden'
        cloned.style.pageBreakAfter = idx < allPages.length - 1 ? 'always' : 'auto'
        cloned.style.pageBreakInside = 'avoid'
        cloned.style.pageBreakBefore = 'auto'
        cloned.style.clear = 'both'
        cloned.style.float = 'none'
        
        // Apply flex to direct children
        const directChildren = cloned.children
        for (let i = 0; i < directChildren.length; i++) {
          const child = directChildren[i]
          child.style.display = 'flex'
          child.style.flexDirection = 'column'
          child.style.flex = '1'
          child.style.minHeight = '0'
        }
        
        // Preserve border from original if it exists
        if (originalComputed.borderWidth && originalComputed.borderWidth !== '0px' && 
            originalComputed.borderStyle && originalComputed.borderStyle !== 'none') {
          cloned.style.border = originalComputed.border
        }
        
        cloned.classList.add('prescription-page')
        clonedPages.push(cloned)
      }
      
      // Build HTML for all pages
      const pagesHTML = clonedPages.map(page => page.outerHTML).join('').trim()
      
      // Extract all CSS from the document - same approach as PrescriptionMakingPage
      const extractAllCSS = () => {
        let css = ''
        
        // Extract from style tags
        const styleTags = document.querySelectorAll('style')
        styleTags.forEach(style => {
          if (style.textContent) {
            css += style.textContent + '\n'
          }
        })
        
        // Extract CSS rules from all stylesheets (including external ones if accessible)
        try {
          for (let i = 0; i < document.styleSheets.length; i++) {
            try {
              const sheet = document.styleSheets[i]
              // Check if we can access cssRules (will throw SecurityError for cross-origin sheets)
              let cssRules = null
              try {
                cssRules = sheet.cssRules
              } catch (e) {
                // Cross-origin stylesheet - cannot access cssRules
                if (sheet.href) {
                  // External stylesheet - will be included as link tag
                  css += `/* External stylesheet: ${sheet.href} */\n`
                }
                continue
              }
              
              if (cssRules) {
                for (let j = 0; j < cssRules.length; j++) {
                  try {
                    css += cssRules[j].cssText + '\n'
                  } catch (e) {
                    // Skip individual rule if inaccessible
                  }
                }
              } else if (sheet.href) {
                // External stylesheet - will be included as link tag
                css += `/* External stylesheet: ${sheet.href} */\n`
              }
            } catch (e) {
              // Cross-origin or inaccessible stylesheet - silently skip
            }
          }
        } catch (e) {
          console.warn('[Rx Drawer Quick Print] Error extracting stylesheets:', e)
        }
        
        return css
      }
      
      const allDocumentCSS = extractAllCSS()
      
      // Check if running in PWA mode
      const isPwaMode = isPWA()
      
      // Get all stylesheet links from current document
      const stylesheetLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(link => {
        try {
          const href = link.href
          if (href && !href.startsWith('data:')) {
            return `<link rel="stylesheet" href="${href}">`
          }
        } catch (e) {
          return ''
        }
        return ''
      }).filter(Boolean).join('\n                ')
      
      // Create print window HTML
      const printHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Prescription Print</title>
            <meta charset="utf-8">
            ${stylesheetLinks}
            <style>
              /* Include all CSS from original document */
              ${allDocumentCSS}
              
              /* Ensure all prescription page styles are preserved */
              .prescription-page,
              .prescription-page * {
                box-sizing: border-box;
              }
              
              ${colorMode === 'bw' 
                ? `
                  @media print {
                    * {
                      -webkit-print-color-adjust: economy !important;
                      print-color-adjust: economy !important;
                      color-adjust: economy !important;
                    }
                    body {
                      filter: grayscale(100%) !important;
                      -webkit-filter: grayscale(100%) !important;
                    }
                  }
                `
                : `
                  @media print {
                    * {
                      -webkit-print-color-adjust: exact !important;
                      print-color-adjust: exact !important;
                      color-adjust: exact !important;
                    }
                  }
                `
              }
              @media print {
                html, body {
                  margin: 0 !important;
                  padding: 0 !important;
                  overflow: visible !important;
                  height: auto !important;
                  background: white !important;
                }
                .prescription-page {
                  display: flex !important;
                  flex-direction: column !important;
                  visibility: visible !important;
                  opacity: 1 !important;
                  page-break-inside: avoid !important;
                  page-break-after: always !important;
                  page-break-before: auto !important;
                  margin: 0 auto !important;
                  padding: 0 !important;
                  width: ${widthOfA4Page}px !important;
                  max-width: ${widthOfA4Page}px !important;
                  min-width: ${widthOfA4Page}px !important;
                  height: ${heightOfA4Page}px !important;
                  min-height: ${heightOfA4Page}px !important;
                  max-height: ${heightOfA4Page}px !important;
                  box-sizing: border-box !important;
                  position: relative !important;
                  clear: both !important;
                  float: none !important;
                  overflow: visible !important;
                }
                .prescription-page:last-child {
                  page-break-after: auto !important;
                }
                .prescription-page > div {
                  display: flex !important;
                  flex-direction: column !important;
                  flex: 1 !important;
                  min-height: 0 !important;
                }
                .prescription-page [data-print-spacer="true"] {
                  flex: 1 1 auto !important;
                  min-height: 0 !important;
                }
                .prescription-page [data-print-footer="true"],
                .prescription-page [style*="margin-top: auto"] {
                  flex-shrink: 0 !important;
                  margin-top: auto !important;
                }
                .prescription-page .A4PageContainer,
                .prescription-page .A4PageMeasureComponentWrapperContainer,
                .prescription-page [id^="a4-"] {
                  padding: 0 !important;
                  margin: 0 !important;
                  width: 100% !important;
                  max-width: 100% !important;
                  min-width: 100% !important;
                  box-sizing: border-box !important;
                  display: flex !important;
                  flex-direction: column !important;
                  flex: 1 !important;
                  min-height: 0 !important;
                  overflow: visible !important;
                }
                .prescription-page * {
                  max-width: 100% !important;
                  box-sizing: border-box !important;
                }
                table {
                  display: table !important;
                  visibility: visible !important;
                  opacity: 1 !important;
                  width: 100% !important;
                  border-collapse: collapse !important;
                  page-break-inside: avoid !important;
                  margin: 0 !important;
                  padding: 0 !important;
                }
                table tr {
                  display: table-row !important;
                  visibility: visible !important;
                  opacity: 1 !important;
                }
                table td,
                table th {
                  display: table-cell !important;
                  visibility: visible !important;
                  opacity: 1 !important;
                }
                @page {
                  size: A4;
                  orientation: portrait;
                  margin: 0;
                  -webkit-print-color-adjust: ${colorMode === 'bw' ? 'economy' : 'exact'} !important;
                  print-color-adjust: ${colorMode === 'bw' ? 'economy' : 'exact'} !important;
                  color-adjust: ${colorMode === 'bw' ? 'economy' : 'exact'} !important;
                }
                body::after {
                  display: none !important;
                  content: '' !important;
                }
                body > *:last-child {
                  page-break-after: auto !important;
                }
              </style>
            </head>
            <body style="margin: 0 !important; padding: 0 !important; overflow: visible !important; height: auto !important; max-height: none !important;">
              ${pagesHTML}
            </body>
          </html>
        `
      
      // Helper function to create and use hidden iframe for printing (PWA fallback)
      const printUsingIframe = () => {
        const printIframe = document.createElement('iframe')
        printIframe.style.position = 'fixed'
        printIframe.style.width = '0'
        printIframe.style.height = '0'
        printIframe.style.border = 'none'
        printIframe.style.opacity = '0'
        printIframe.style.pointerEvents = 'none'
        printIframe.style.zIndex = '-1'
        document.body.appendChild(printIframe)
        
        printIframe.contentDocument.open()
        printIframe.contentDocument.write(printHTML)
        printIframe.contentDocument.close()
        
        printIframe.onload = () => {
          setTimeout(() => {
            printIframe.contentWindow.print()
            console.log('[Rx Drawer Quick Print] Print dialog opened from iframe')
          }, 500)
        }
      }
      
      // Try to open new window, fallback to iframe for PWA
      if (isPwaMode) {
        printUsingIframe()
      } else {
        let printWindow
        try {
          printWindow = window.open('', '_blank', 'width=800,height=600')
        } catch (e) {
          console.warn('[Rx Drawer Quick Print] window.open() threw error - popup blocked:', e)
          printWindow = null
        }
        
        // If window.open() failed or was blocked, use iframe approach immediately
        if (!printWindow) {
          console.log('[Rx Drawer Quick Print] Popup blocked or window.open() failed, using iframe fallback')
          printUsingIframe()
          return
        }
        
        // Verify window is accessible before proceeding
        try {
          if (printWindow.closed) {
            console.log('[Rx Drawer Quick Print] Window is closed, using iframe fallback')
            printUsingIframe()
            return
          }
          // Try to access document - if this throws, use iframe fallback
          if (!printWindow.document) {
            console.log('[Rx Drawer Quick Print] Cannot access window.document, using iframe fallback')
            printUsingIframe()
            return
          }
        } catch (e) {
          console.log('[Rx Drawer Quick Print] Error verifying window access, using iframe fallback:', e)
          printUsingIframe()
          return
        }
        
        printWindow.document.open()
        printWindow.document.write(printHTML)
        printWindow.document.close()
        
        // Wait for stylesheets to load, then trigger print
        const waitForStylesheets = (callback, maxWait = 1500) => {
          const startTime = Date.now()
          const checkStylesheets = () => {
            try {
              const stylesheets = printWindow.document.querySelectorAll('link[rel="stylesheet"]')
              let loadedCount = 0
              let totalCount = stylesheets.length
              
              if (totalCount === 0) {
                callback()
                return
              }
              
              stylesheets.forEach(link => {
                try {
                  const sheet = link.sheet
                  if (sheet && sheet.cssRules) {
                    loadedCount++
                  } else if (link.onload) {
                    loadedCount++
                  } else {
                    link.onload = () => {
                      loadedCount++
                      if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
                        callback()
                      }
                    }
                    link.onerror = () => {
                      loadedCount++
                      if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
                        callback()
                      }
                    }
                  }
                } catch (e) {
                  loadedCount++
                }
              })
              
              if (loadedCount === totalCount || Date.now() - startTime > maxWait) {
                callback()
              } else {
                setTimeout(checkStylesheets, 100)
              }
            } catch (e) {
              // If we can't access iframe document, proceed anyway
              callback()
            }
          }
          
          printWindow.onload = () => {
            setTimeout(checkStylesheets, 100)
          }
          
          // Fallback if onload doesn't fire
          setTimeout(() => {
            checkStylesheets()
          }, 500)
        }
        
        waitForStylesheets(() => {
          // Ensure prescription pages have correct width
          try {
            const body = printWindow.document.body
            const prescriptionPages = body.querySelectorAll('.prescription-page')
            prescriptionPages.forEach(page => {
              page.style.width = `${widthOfA4Page}px`
              page.style.maxWidth = `${widthOfA4Page}px`
              page.style.minWidth = `${widthOfA4Page}px`
            })
          } catch (e) {
            console.warn('[Rx Drawer Quick Print] Could not adjust page widths:', e)
          }
          
          setTimeout(() => {
            printWindow.print()
          }, 300)
        })
      }
      
    } catch (error) {
      console.error('[Rx Drawer Quick Print] Error:', error)
      toast.error('Failed to prepare pages for printing. Please try again.', { autoClose: 3000 })
    }
  }

  // Print functions - use ref if available, otherwise generate PDF from drawer
  const handleSaveAndPrint = async (selectedPages = null) => {
    console.log('[Rx Drawer] handleSaveAndPrint called', { 
      hasRef: !!prescriptionRef, 
      hasCurrent: !!prescriptionRef?.current,
      hasSaveAndPrintDirect: !!prescriptionRef?.current?.saveAndPrintDirect,
      hasSaveAndPrint: !!prescriptionRef?.current?.saveAndPrint,
      selectedPages 
    })
    
    // Set loading state
    setIsPrinting(true)
    
    // Fallback timeout to reset loading state if print dialog doesn't open (e.g., error)
    if (printTimeoutRef.current) {
      window.clearTimeout(printTimeoutRef.current)
    }
    printTimeoutRef.current = window.setTimeout(() => {
      console.log('[Rx Drawer] Timeout: Resetting loading state (print dialog may not have opened)')
      setIsPrinting(false)
      printTimeoutRef.current = null
    }, 30000) // 30 second timeout as fallback
    
    try {
      // Prefer saveAndPrintDirect (skips drawer) if available, fallback to saveAndPrint
      if (prescriptionRef?.current?.saveAndPrintDirect) {
        console.log('[Rx Drawer] Calling saveAndPrintDirect via ref (skips drawer)')
        await prescriptionRef.current.saveAndPrintDirect(selectedPages)
        console.log('[Rx Drawer] saveAndPrintDirect completed')
      } else if (prescriptionRef?.current?.saveAndPrint) {
        console.log('[Rx Drawer] saveAndPrintDirect not available, using saveAndPrint via ref')
        await prescriptionRef.current.saveAndPrint(selectedPages)
        console.log('[Rx Drawer] saveAndPrint completed')
      } else {
        console.log('[Rx Drawer] prescriptionRef not available, generating PDF from drawer')
        // Generate PDF from drawer and auto-print
        await generatePDFFromDrawer(selectedPages, true)
      }
    } catch (error) {
      console.error('[Rx Drawer] Error in handleSaveAndPrint:', error)
      setIsPrinting(false)
      if (printTimeoutRef.current) {
        window.clearTimeout(printTimeoutRef.current)
        printTimeoutRef.current = null
      }
      throw error
    }
  }

  // Get context data (may be empty, but we'll use it if available)
  const {
    prescriptionData: contextPrescriptionData,
    allVitalsOfThisPatient: contextAllVitals,
    jointHomunculusData: contextJointHomunculusData,
  } = useContext(PrescriptionDataContext)

  // Extract prescription data - prioritize metaPrescriptionData, fallback to context
  const prescriptionDataToUse = metaPrescriptionData || contextPrescriptionData

  // Extract all selected items from prescription data structure using helper functions
  const extractedData = useMemo(() => {
    if (!prescriptionDataToUse) return {}

    return {
      selectedObservations: getInitialDataForSettingExistingSelectedObservations(
        prescriptionDataToUse?.prescriptionData?.symptomsAndObservations,
      ),
      selectedComplaints: getInitialDataForSettingExistingSelectedComplaints(
        prescriptionDataToUse?.prescriptionData?.complaints,
      ),
      selectedDiagnosis: getInitialDataForSettingExistingSelectedDiagnosis(
        prescriptionDataToUse?.prescriptionData?.diagnosis,
      ),
      drugsAdvice: getInitialDataForSettingExistingDrugAdvice(
        prescriptionDataToUse?.prescriptionData?.drugInfoList,
      ),
      selectedAdvice: getInitialDataForSettingExistingSelectedAdvice(
        prescriptionDataToUse?.prescriptionData?.guidanceAndAdvices,
      ),
      selectedTherapies: getInitialDataForSettingExistingSelectedTherapies(
        prescriptionDataToUse?.prescriptionData?.therapiesNeeded,
      ),
      nextVisitInputValue: getInitialDataForSettingDaysOfNextVisit(
        prescriptionDataToUse?.prescriptionData?.nextVisitDate,
        prescriptionDataToUse?.prescriptionData?.createdTime,
      ),
      selectedInvestigations: getInitialDataForSettingExistingSelectedInvestigations(
        prescriptionDataToUse?.prescriptionData?.investigations,
      ),
      selectedInstructions: getInitialDataForSettingExistingSelectedInstructions(
        prescriptionDataToUse?.prescriptionData?.instructions,
      ),
      selectedAssessments: getInitialDataForSettingExistingHealthEntity(
        prescriptionDataToUse?.prescriptionData?.psychAssessments,
      ),
      suggestedBookingTherapy: prescriptionDataToUse?.prescriptionData?.suggestedBookingTherapy || false,
      finalRequestedTestsBy: prescriptionDataToUse?.prescriptionData?.finishRequestedTestsBy,
      referredToDoctorName: prescriptionDataToUse?.prescriptionData?.referredToDoctorName,
      jointHomunculusData: prescriptionDataToUse?.prescriptionData?.jointHomunculusData || contextJointHomunculusData,
      patientHistoryNotes: prescriptionDataToUse?.prescriptionData?.patientHistoryNotes,
    }
  }, [prescriptionDataToUse, contextJointHomunculusData])

  // Get vitals - prioritize context, fallback to finding from bookedEventData
  const vital = useMemo(() => {
    if (contextAllVitals) {
      return contextAllVitals.find((item) => item?.bookedEventId === bookedEventData?.id)
    }
    return null
  }, [contextAllVitals, bookedEventData?.id])
  
  // Fix for Rx drawer: Scroll through content when drawer opens to ensure all pages render
  // This is the same issue as Quick Print - dimensions are measured using getBoundingClientRect()
  // which returns 0 for elements outside viewport. Scrolling forces all content into viewport.
  // We use the same multi-step approach as Quick Print:
  // 1. Scroll through drawer content to force all pages to render
  // 2. Wait for dimensions to update
  // 3. Wait for multipass algorithm to complete (checking if totalPages increases)
  useEffect(() => {
    if (!prescriptionDataToUse || !scrollableContentRef.current) {
      return
    }
    
    // Capture initial totalPages before scrolling (use ref for consistency)
    const initialTotalPages = totalPagesRef.current
    
    const scrollableContainer = scrollableContentRef.current
    
    // STEP 1: Scroll through drawer content to force all pages to render
    const scrollToRenderAllPages = () => {
      return new Promise((resolve) => {
        const initialPageCount = scrollableContainer.querySelectorAll('.prescription-page').length
        
        const maxScroll = Math.max(
          scrollableContainer.scrollHeight,
          scrollableContainer.offsetHeight,
          scrollableContainer.clientHeight
        )
        
        // Scroll through the drawer content in steps to force all pages to render
        let currentScroll = 0
        const scrollStep = scrollableContainer.clientHeight * 0.8 // Scroll 80% of container height each step
        const scrollDelay = 100 // Wait 100ms between scrolls
        
        const scrollStepByStep = () => {
          if (currentScroll < maxScroll) {
            currentScroll = Math.min(currentScroll + scrollStep, maxScroll)
            scrollableContainer.scrollTop = currentScroll
            
            window.setTimeout(() => {
              if (currentScroll < maxScroll) {
                scrollStepByStep()
              } else {
                // Scroll back to top
                scrollableContainer.scrollTop = 0
                window.setTimeout(() => {
                  // Trigger window resize event to force dimension hooks to refresh
                  // useElementDimensions listens to resize events, which will refresh all dimensions
                  window.dispatchEvent(new Event('resize'))
                  
                  // Also trigger a minimal window scroll to ensure scroll listeners fire
                  const currentScrollY = window.scrollY
                  window.scrollTo({ top: currentScrollY + 1, behavior: 'auto' })
                  window.requestAnimationFrame(() => {
                    window.scrollTo({ top: currentScrollY, behavior: 'auto' })
                  })
                  
                  // Force dimension refresh by updating refreshDimensionsDependencies
                  setDimensionRefreshTrigger(prev => prev + 1)
                  
                  resolve()
                }, 200)
              }
            }, scrollDelay)
          } else {
            resolve()
          }
        }
        
        // Start scrolling after a short delay to ensure drawer is fully rendered
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.setTimeout(() => {
              scrollStepByStep()
            }, 300)
          })
        })
      })
    }
    
    // STEP 2: Wait for dimensions to update after scrolling
    // STEP 3: Wait for multipass algorithm to complete (checking if totalPages increases)
    const waitForMultipassAlgorithm = () => {
      return new Promise((resolve) => {
        const maxWait = 10000 // Max 10 seconds
        const startTime = Date.now()
        let lastTotalPages = initialTotalPages
        let stableCount = 0
        const requiredStableChecks = 3
        
        const checkTotalPages = () => {
          // Use ref to get latest totalPages value (closure-safe)
          const currentTotalPages = totalPagesRef.current
          
          // If totalPages increased, dimensions updated and algorithm recalculated
          if (currentTotalPages > initialTotalPages) {
            resolve()
            return
          }
          
          // If totalPages is stable and > 0, algorithm has completed
          if (currentTotalPages > 0) {
            if (currentTotalPages === lastTotalPages) {
              stableCount++
              if (stableCount >= requiredStableChecks) {
                resolve()
                return
              }
            } else {
              stableCount = 0 // Reset if changed
            }
          }
          
          lastTotalPages = currentTotalPages
          
          // Check if we've exceeded max wait time
          if (Date.now() - startTime > maxWait) {
            resolve()
            return
          }
          
          // Continue checking
          window.requestAnimationFrame(() => {
            window.setTimeout(checkTotalPages, 200) // Check every 200ms
          })
        }
        
        // Start checking after a short delay
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            window.setTimeout(checkTotalPages, 300)
          })
        })
      })
    }
    
    // Wait for drawer content to render, then execute steps
    const timer = window.setTimeout(() => {
      scrollToRenderAllPages().then(() => {
        // STEP 2: Wait for dimensions to update after scrolling
        // useElementDimensions hooks listen to window scroll/resize events, not container scroll
        // We triggered window resize/scroll events, now wait for React to process dimension updates
        return new Promise((resolve) => {
          // Wait longer to ensure dimension hooks have processed the resize/scroll events
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              window.setTimeout(() => {
                // Trigger another resize event to ensure dimensions refresh
                window.dispatchEvent(new Event('resize'))
                
                window.requestAnimationFrame(() => {
                  window.setTimeout(() => {
                    resolve()
                  }, 300) // Additional wait for dimension hooks to process
                })
              }, 500) // Give time for dimension hooks to update
            })
          })
        })
      }).then(() => {
        // STEP 3: Wait for multipass algorithm to complete
        return waitForMultipassAlgorithm()
      }).then(() => {
        // STEP 4: Wait for all pages to be rendered in the DOM
        // After multipass algorithm completes, React needs time to render all pages
        const waitForAllPages = () => {
          return new Promise((resolve) => {
            const expectedPageCount = totalPagesRef.current || 0
            
            if (expectedPageCount === 0) {
              resolve()
              return
            }
            
            let lastPageCount = 0
            let stableCount = 0
            const requiredStableChecks = 3
            const maxWait = 5000 // Max 5 seconds
            
            const startTime = Date.now()
            
            const checkPages = () => {
              // Check for pages within the drawer container only
              const currentPages = Array.from(scrollableContainer.querySelectorAll('.prescription-page')).filter(page => {
                const pageStyle = window.getComputedStyle(page)
                if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
                  return false
                }
                let parent = page.parentElement
                while (parent && parent !== scrollableContainer) {
                  const style = window.getComputedStyle(parent)
                  if (style.display === 'none' || style.visibility === 'hidden') {
                    return false
                  }
                  parent = parent.parentElement
                }
                return true
              })
              
              const currentCount = currentPages.length
              
              // Check if we have the expected number of pages
              if (currentCount >= expectedPageCount && currentCount === lastPageCount) {
                stableCount++
                if (stableCount >= requiredStableChecks) {
                  resolve()
                  return
                }
              } else if (currentCount < expectedPageCount) {
                stableCount = 0 // Reset if we don't have enough pages yet
              } else if (currentCount === lastPageCount && currentCount > 0) {
                // If count is stable but less than expected, still wait a bit more
                stableCount++
                if (stableCount >= requiredStableChecks * 2) {
                  resolve()
                  return
                }
              } else {
                stableCount = 0 // Reset if count changed
              }
              
              lastPageCount = currentCount
              
              // Check if we've exceeded max wait time
              if (Date.now() - startTime > maxWait) {
                resolve()
                return
              }
              
              // Continue checking
              window.requestAnimationFrame(() => {
                window.setTimeout(checkPages, 200) // Check every 200ms
              })
            }
            
            // Start checking after a short delay to allow React to render
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                window.setTimeout(checkPages, 300)
              })
            })
          })
        }
        
        return waitForAllPages()
      })
    }, 500) // Wait 500ms for drawer to fully render
    
    return () => {
      window.clearTimeout(timer)
    }
  }, [prescriptionDataToUse]) // Re-run if prescription data changes
  
  // Update ref whenever totalPages changes (so closures can access latest value)
  useEffect(() => {
    totalPagesRef.current = totalPages
  }, [totalPages])
  //   return (
  //     <>
  //       <div className="d-flex align-items-center justify-content-between">
  //         <Button
  //           variant="contained"
  //           onClick={() => {
  //             window.print()
  //           }}
  //         >
  //           Print
  //         </Button>
  //       </div>
  //       <ViewOnlyPrescriptionV2
  //         hidePrintMarginsControl
  //         prescriptionViewSettings={prescriptionViewSettingsData}
  //         doctorEmrSections={emrSectionsData}
  //         prescriptionData={auxiliaryData?.metaPrescription}
  //       />
  //     </>
  //   )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header section - fixed at top */}
      <Box sx={{ flexShrink: 0, overflow: 'visible', pb: 2 }}>
        <div className="d-flex align-items-center justify-content-between gap-2 mb-4 mx-3 hide-on-print-only" style={{ minHeight: '56px', paddingTop: '8px', paddingBottom: '8px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
            {/* B&W / Colored Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                B&W
              </Typography>
              <Switch
                checked={colorMode === 'colored'}
                onChange={(e) => handleColorModeChange(e.target.checked ? 'colored' : 'bw')}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#4338ca',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#4338ca',
                  },
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                Colored
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <PageSelectionMenu
                totalPages={totalPages}
                onPrint={handleSaveAndPrint}
                disabled={totalPages === 0}
              />
            </Box>
            <Button
              variant="outlined"
              color="emrSecondary"
              onClick={handleQuickPrint}
              disabled={totalPages === 0}
              endIcon={<PrintOutlined />}
              sx={{
                borderWidth: 2,
                flexShrink: 0,
                '&:hover': {
                  borderWidth: 2,
                },
              }}
            >
              Quick Print
            </Button>
            {/* Print All button hidden per user request */}
            {false && (
              <Button
                variant="contained"
                color="emrSecondary"
                onClick={() => handleSaveAndPrint(null)}
                disabled={isPrinting || totalPages === 0}
                endIcon={isPrinting ? <CircularProgress size={16} color="inherit" /> : <Print />}
              >
                {isPrinting ? 'Preparing...' : 'Print All'}
              </Button>
            )}
          </Box>
        </div>
        {!printSettings?.useOldPrintMethod && (
          <div className="mr-2">
            <div className="mx-3 hide-on-print-only" style={{ flex: 1 }}>
              <A4PrintSettings
                printSettings={printSettings}
                setPrintSettings={setPrintSettings}
                hideMarginSelection
                clinicBrandId={bookedEventData?.clinicBrandId}
              />
            </div>
          </div>
        )}
      </Box>
      {/* Scrollable content section */}
      <Box 
        ref={scrollableContentRef}
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'auto', 
          minHeight: 0,
          width: '100%',
          '& .PMPPrintWrapper': {
            overflow: 'visible !important',
            height: 'auto !important',
          }
        }}
      >
        <PMPPrintWrapper showInUI={true}>
          {!prescriptionDataToUse ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No prescription data available for this visit.
              </Typography>
            </Box>
          ) : (
            <PrescriptionViewComponent
              showInUI={true}
              debugMode={false}
              prescriptionData={prescriptionDataToUse}
              setPrescriptionData={() => {}} // Read-only, no setter needed
              selectedObservations={extractedData.selectedObservations}
              selectedComplaints={extractedData.selectedComplaints}
              selectedDiagnosis={extractedData.selectedDiagnosis}
              selectedAdvice={extractedData.selectedAdvice}
              vital={vital}
              selectedInstructions={extractedData.selectedInstructions}
              selectedTherapies={extractedData.selectedTherapies}
              selectedAssessments={extractedData.selectedAssessments}
              finalRequestedTestsBy={extractedData.finalRequestedTestsBy}
              referredToDoctorName={extractedData.referredToDoctorName}
              nextVisitInputType="days"
              nextVisitInputValue={extractedData.nextVisitInputValue}
              prescriptionViewSettings={prescriptionViewSettings}
              jointHomunculusData={extractedData.jointHomunculusData}
              printSettings={printSettings}
              printType={prescriptionDataToUse?.doctorPrintType || undefined} // Extract from prescription data
              emrSections={emrSectionsData}
              drugsAdvice={extractedData.drugsAdvice}
              honorPropValue={false}
              selectedInvestigations={extractedData.selectedInvestigations}
              setPropValue={() => null}
              setValue={() => null}
              suggestedBookingTherapy={extractedData.suggestedBookingTherapy}
              useOldUI={printSettings?.useOldPrintMethod}
              value=""
              prescriptionTemplateData={prescriptionDataToUse}
              refreshDimensionsDependencies={[printSettings, prescriptionViewSettings, prescriptionDataToUse, dimensionRefreshTrigger]}
              patientHistoryNotes={extractedData.patientHistoryNotes}
              onTotalPagesChange={setTotalPages}
              colorMode={colorMode}
            />
          )}
        </PMPPrintWrapper>
      </Box>
    </Box>
  )
}
